import { DraggableAttributes } from "@dnd-kit/core";
import { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";
import { ReactNode, type JSX } from "react";

export enum ColumnMode {
  Single = "Single",
  Double = "Double",
}

export type Column<T = string, U = Record<string, unknown> | undefined> = Array<{
  value: T;
  hidden?: boolean;
  // Listed, but neither draggable nor a drop target
  disabled?: boolean;
  props?: U;
}>;

export enum ColumnId {
  First = "first",
  Second = "second",
}
export type DragDropOnChangeProps<
  T extends string,
  U extends Record<string, unknown> | undefined,
> = { first: Column<T, U>; second: Column<T, U> };

export type DragDropListItemDefaultProps = {
  children?: ReactNode;
  dragging?: boolean;
  handleListeners?: SyntheticListenerMap;
  handleAttributes?: DraggableAttributes;
  handleRef?: React.ForwardedRef<HTMLDivElement>;
  titleComponent?: ReactNode;
  disabled?: boolean;
};

export type RenderItemContentProps<
  T extends string,
  U extends Record<string, unknown> | undefined,
> = {
  id: T;
  dragging?: boolean;
  handleListeners?: SyntheticListenerMap;
  handleAttributes?: DraggableAttributes;
  handleRef?: React.ForwardedRef<HTMLDivElement>;
  disabled?: boolean;
  props: U;
  columnId?: ColumnId;
};

export type DragDropListDraggableItemProps<
  T extends string,
  U extends Record<string, unknown> | undefined,
> = {
  id: T;
  className?: string;
  disabled?: boolean;
  titleComponent?: ReactNode;
  component?: ReactNode;
  noBorder?: boolean;
  size?: "small" | "medium";
  renderItemProps: U;
  columnId?: ColumnId;
  renderItemContent: (props: RenderItemContentProps<T, U>) => ReactNode;
};

export type DragDropListProps<T extends string, U extends Record<string, unknown> | undefined> = {
  columnMode: ColumnMode;
  children?: ReactNode;
  onChange: (props: DragDropOnChangeProps<T, U>) => void;
  onSelect?: (columnId: ColumnId, id: T, isSelected: boolean) => void;
  firstColumn: Column<T, U>;
  secondColumn?: Column<T, U>;
  itemClassName?: string;
  emptyState?: JSX.Element;
  vertical?: boolean;
  containerClassName?: string;
  hasDroppableCardWrapper?: boolean;
  firstColumnTitle?: string;
  secondColumnTitle?: string;
  firstColumnMinItems?: number;
  secondColumnMinItems?: number;
  verticalAxisOnly?: boolean;
  disabledItemsInsideContainer?: T[];
  renderEmptyColumnState?: (isDragActive: boolean, columnId: ColumnId) => JSX.Element | null;
  renderItemContent: (props: RenderItemContentProps<T, U>) => ReactNode;
  itemNoBorder?: boolean;
  itemSize?: "small" | "medium";
  emptyContainerOffset?: number;
};
